import { createHash } from "crypto";
import { sql } from "drizzle-orm";
import {
  createGencowProcedureBuilders,
  createWorkflowRealtimeToken,
  getWorkflowResumeActionName,
  serializeWorkflowValue,
  v,
  workflow,
  type DocumentConvertMode,
  type DocumentConvertProvider,
  type DocumentSection,
  type DocumentVisibility,
  type GencowCtx,
  type RagIngestInput,
  type RagIngestJobStatus,
  type RagIngestStartResult,
  type RagIngestWorkflowArgs,
  type WorkflowCtx,
} from "@gencow/core";
import { resolveDocumentSourceKey } from "./document-convert-local.js";
import {
  createRagEmbeddingBatches,
  fetchOpenAICompatibleEmbeddings,
  resolveRagEmbeddingTargets,
} from "./rag-embedding-runtime.js";
import { emitCompletedRagIngestMetrics } from "./rag-ingest-metrics.js";
import { ensureRagCorpusPolicy } from "./rag-ingest-policy.js";
import { registerRagCorporaRuntime } from "./rag-corpora-runtime.js";
import { registerRagOperationsRuntime } from "./rag-operations-runtime.js";
import {
  createQueuedRagIngestJob,
  finalizeRagIngestJob,
  findLatestRagIngestJobBySourceId,
  findRagSourceByScopeAndKey,
  listAccessibleRagIngestJobs,
  loadAccessibleRagIngestJob,
  loadAccessibleRagSource,
  markRagSourceFailed,
  persistConvertedRagSource,
  replaceRagSectionsAndChunks,
  resetRagIngestJobForRetry,
  setRagIngestJobStage,
  upsertQueuedRagSource,
} from "./rag-ingest-jobs.js";
import {
  RAG_BUILTIN_OPERATION_NAMES,
  ragBuiltinInputSchemas,
  ragBuiltinOutputSchemas,
} from "./builtins/rag/schemas.js";

const INTERNAL_RAG_INGEST_WORKFLOW = "__gencow.documents.ingest.run";
const SUPPORTED_VISIBILITIES = new Set<DocumentVisibility>(["private", "shared", "public"]);
const SUPPORTED_REINDEX_MODES = new Set(["if_changed", "force"]);
const SUPPORTED_MODES = new Set<DocumentConvertMode>([
  "auto",
  "text-only",
  "no-external-provider",
  "prefer-external",
  "force-external",
  "force-ocr",
]);
const SUPPORTED_PROVIDERS = new Set<DocumentConvertProvider>([
  "auto",
  "local_text",
  "gemini",
  "openai",
  "ocr",
  "custom_vlm",
]);
const SUPPORTED_JOB_STATUSES = new Set<RagIngestJobStatus>([
  "queued",
  "converting",
  "chunking",
  "embedding",
  "upserting",
  "completed",
  "failed",
  "canceled",
]);
const CHUNK_TARGET_CHARS = 1400;
const CHUNK_OVERLAP_CHARS = 180;
const procedure = createGencowProcedureBuilders();

type ChunkPlan = {
  id: string;
  sectionId: string | null;
  chunkIndex: number;
  chunkText: string;
  lexicalText: string;
  pageStart: number | null;
  pageEnd: number | null;
  chunkChecksum: string;
  metadata: Record<string, unknown>;
};

type StoredFileLike = {
  id: string;
  name: string;
  type: string;
  size: number;
  buffer: Buffer;
};

function normalizeVisibility(value: string): DocumentVisibility {
  if (!SUPPORTED_VISIBILITIES.has(value as DocumentVisibility)) {
    throw new Error(`Unsupported visibility "${value}"`);
  }
  return value as DocumentVisibility;
}

function normalizeReindexMode(value: string | undefined): "if_changed" | "force" {
  if (!value) return "if_changed";
  if (!SUPPORTED_REINDEX_MODES.has(value)) {
    throw new Error(`Unsupported reindexMode "${value}"`);
  }
  return value as "if_changed" | "force";
}

function normalizeMode(value: string | undefined): DocumentConvertMode | undefined {
  if (!value) return undefined;
  if (!SUPPORTED_MODES.has(value as DocumentConvertMode)) {
    throw new Error(`Unsupported convert mode "${value}"`);
  }
  return value as DocumentConvertMode;
}

function normalizeProvider(value: string | undefined): DocumentConvertProvider | undefined {
  if (!value) return undefined;
  if (!SUPPORTED_PROVIDERS.has(value as DocumentConvertProvider)) {
    throw new Error(`Unsupported convert provider "${value}"`);
  }
  return value as DocumentConvertProvider;
}

function normalizeMetadata(value: unknown): Record<string, unknown> {
  if (!value || typeof value !== "object" || Array.isArray(value)) {
    return {};
  }
  return value as Record<string, unknown>;
}

function normalizeJobStatus(value: string | undefined): RagIngestJobStatus | undefined {
  if (!value) return undefined;
  if (!SUPPORTED_JOB_STATUSES.has(value as RagIngestJobStatus)) {
    throw new Error(`Unsupported ingest status "${value}"`);
  }
  return value as RagIngestJobStatus;
}

function resolveOwnerUserId(input: {
  visibility: DocumentVisibility;
  ownerUserId?: string;
  viewerUserId: string;
}): string | null {
  if (input.visibility !== "private") {
    return input.ownerUserId?.trim() || null;
  }
  const requestedOwner = input.ownerUserId?.trim();
  if (requestedOwner && requestedOwner !== input.viewerUserId) {
    throw new Error("ownerUserId must match the authenticated user for private ingest");
  }
  return requestedOwner ?? input.viewerUserId;
}

function rowsFromResult<T>(result: unknown): T[] {
  if (Array.isArray(result)) return result as T[];
  if (result && typeof result === "object" && Array.isArray((result as { rows?: unknown[] }).rows)) {
    return (result as { rows: T[] }).rows;
  }
  return [];
}

function sanitizeText(value: string): string {
  return value
    .replace(/\r\n/g, "\n")
    .replace(/\r/g, "\n")
    .replace(/[ \t]+\n/g, "\n")
    .replace(/\n{3,}/g, "\n\n")
    .trim();
}

function resolveChunkPages(section: DocumentSection | undefined): {
  pageStart: number | null;
  pageEnd: number | null;
} {
  return { pageStart: section?.pageStart ?? null, pageEnd: section?.pageEnd ?? null };
}

function createChunkPlan(markdown: string, sections: DocumentSection[]): ChunkPlan[] {
  const safeSections =
    sections.length > 0
      ? sections
      : [
          {
            id: "section-1",
            depth: 1,
            path: [],
            charStart: 0,
            charEnd: markdown.length,
          },
        ];
  const chunks: ChunkPlan[] = [];

  for (const section of safeSections) {
    const sectionStart = Math.max(0, section.charStart);
    const sectionEnd = Math.max(sectionStart, Math.min(markdown.length, section.charEnd));
    const rawSectionText = sanitizeText(markdown.slice(sectionStart, sectionEnd));
    if (!rawSectionText) continue;

    const pages = resolveChunkPages(section);
    let localIndex = 0;
    let cursor = 0;

    while (cursor < rawSectionText.length) {
      const next = Math.min(rawSectionText.length, cursor + CHUNK_TARGET_CHARS);
      const rawChunk = sanitizeText(rawSectionText.slice(cursor, next));
      if (rawChunk) {
        const chunkIndex = chunks.length;
        chunks.push({
          id: `chunk-${chunkIndex + 1}`,
          sectionId: section.id,
          chunkIndex,
          chunkText: rawChunk,
          lexicalText: rawChunk.toLowerCase(),
          pageStart: pages.pageStart,
          pageEnd: pages.pageEnd,
          chunkChecksum: createHash("sha256").update(rawChunk).digest("hex"),
          metadata: {
            sectionPath: section.path,
            sectionDepth: section.depth,
            localIndex,
          },
        });
        localIndex += 1;
      }
      if (next >= rawSectionText.length) break;
      cursor = Math.max(next - CHUNK_OVERLAP_CHARS, cursor + 1);
    }
  }

  return chunks.length > 0
    ? chunks
    : [
        {
          id: "chunk-1",
          sectionId: safeSections[0]?.id ?? null,
          chunkIndex: 0,
          chunkText: sanitizeText(markdown),
          lexicalText: sanitizeText(markdown).toLowerCase(),
          pageStart: safeSections[0]?.pageStart ?? null,
          pageEnd: safeSections[0]?.pageEnd ?? null,
          chunkChecksum: createHash("sha256").update(markdown).digest("hex"),
          metadata: {},
        },
      ];
}

async function embedChunks(wf: WorkflowCtx, chunks: ChunkPlan[]): Promise<Array<number[] | null>> {
  if (chunks.length === 0) return [];
  const targets = resolveRagEmbeddingTargets();
  if (targets.length === 0) return chunks.map(() => null);

  const batches = createRagEmbeddingBatches(chunks);
  const embedded = await wf.parallel(
    batches.map(
      (batch) => async () =>
        fetchOpenAICompatibleEmbeddings({
          targets,
          texts: batch.map((chunk) => chunk.chunkText),
        }),
    ),
  );
  return embedded.flat();
}

function currentProvider(provider: DocumentConvertProvider | undefined): string {
  if (!provider || provider === "auto") return "local_text";
  return provider;
}

async function insertWorkflowRun(
  ctx: GencowCtx,
  params: {
    workflowId: string;
    args: RagIngestWorkflowArgs;
    viewerUserId: string;
  },
): Promise<string> {
  const persistedArgs = serializeWorkflowValue(params.args);
  const realtimeToken = createWorkflowRealtimeToken();

  await ctx.unsafeDb.execute(sql`
        INSERT INTO _gencow_workflows (
          id,
          name,
          args,
          realtime_token,
          status,
          retry_count,
          max_retries,
          max_duration_ms,
          user_id
        )
        VALUES (
          ${params.workflowId},
          ${INTERNAL_RAG_INGEST_WORKFLOW},
          ${JSON.stringify(persistedArgs)}::jsonb,
          ${realtimeToken},
          'pending',
          0,
          3,
          1800000,
          ${params.viewerUserId}
        )
    `);

  return ctx.scheduler.runAfter(0, getWorkflowResumeActionName(INTERNAL_RAG_INGEST_WORKFLOW), {
    workflowId: params.workflowId,
  }) as string;
}

async function startRagIngestJob(ctx: GencowCtx, input: RagIngestInput): Promise<RagIngestStartResult> {
  const viewer = ctx.auth.requireAuth();
  if (!input.corpus?.trim()) {
    throw new Error("corpus is required");
  }
  const visibility = input.visibility;
  const ownerUserId = resolveOwnerUserId({
    visibility,
    ownerUserId: input.ownerUserId,
    viewerUserId: viewer.id,
  });

  const stored = (await ctx.storage.get(input.storageId)) as StoredFileLike | null;
  if (!stored) {
    throw new Error(`storageId not found: ${input.storageId}`);
  }

  const sourceKey = resolveDocumentSourceKey(
    { storageId: input.storageId, filename: input.sourceKey?.trim() || undefined },
    stored.name,
  );
  const checksum = createHash("sha256").update(stored.buffer).digest("hex");
  const existingSource = await findRagSourceByScopeAndKey(ctx.unsafeDb, {
    corpus: input.corpus,
    visibility,
    ownerUserId,
    sourceKey,
  });

  const reindexMode = input.reindexMode ?? "if_changed";
  if (reindexMode === "if_changed" && existingSource && existingSource.source_checksum === checksum) {
    const existingJob = await findLatestRagIngestJobBySourceId(ctx.unsafeDb, existingSource.id);
    if (existingJob) {
      return {
        workflowId: existingJob.workflowId,
        jobId: existingJob.id,
        sourceId: existingSource.id,
        status: "queued",
      };
    }
  }

  const workflowId = crypto.randomUUID();
  const sourceId = existingSource?.id ?? crypto.randomUUID();
  const metadata = normalizeMetadata(input.metadata);
  await ensureRagCorpusPolicy({
    db: ctx.unsafeDb as never,
    corpus: input.corpus,
    visibility,
    ownerUserId,
    metadata,
  });
  await upsertQueuedRagSource(ctx.unsafeDb, {
    sourceId,
    corpus: input.corpus,
    visibility,
    ownerUserId,
    storageId: input.storageId,
    sourceKey,
    sourceTitle: stored.name,
    mimeType: stored.type || "application/octet-stream",
    byteSize: stored.size,
    checksum,
    provider: currentProvider(input.provider),
    metadata,
  });
  await createQueuedRagIngestJob(ctx.unsafeDb, {
    jobId: workflowId,
    workflowId,
    sourceId,
    corpus: input.corpus,
    visibility,
    ownerUserId,
  });

  try {
    await insertWorkflowRun(ctx, {
      workflowId,
      viewerUserId: viewer.id,
      args: {
        jobId: workflowId,
        sourceId,
        storageId: input.storageId,
        corpus: input.corpus,
        visibility,
        ownerUserId: ownerUserId ?? undefined,
        sourceKey,
        metadata,
        reindexMode,
        mode: input.mode,
        provider: input.provider,
      },
    });
  } catch (error) {
    await ctx.unsafeDb.execute(sql`DELETE FROM rag_ingest_jobs WHERE id = ${workflowId}`);
    if (!existingSource) {
      await ctx.unsafeDb.execute(sql`DELETE FROM rag_sources WHERE id = ${sourceId}`);
    }
    await ctx.unsafeDb.execute(sql`DELETE FROM _gencow_workflows WHERE id = ${workflowId}`);
    throw error;
  }

  return {
    workflowId,
    jobId: workflowId,
    sourceId,
    status: "queued",
  };
}

async function setWorkflowStatusAndSchedule(
  ctx: GencowCtx,
  params: {
    workflowId: string;
    updateSql: ReturnType<typeof sql>;
    action: "retry" | "resume";
  },
) {
  const result = await ctx.unsafeDb.execute(params.updateSql);
  const row = rowsFromResult<{ id: string }>(result)[0] ?? null;
  if (!row) {
    return { ok: false, workflowId: params.workflowId, scheduledJobId: null, action: params.action };
  }
  const scheduledJobId = ctx.scheduler.runAfter(
    0,
    getWorkflowResumeActionName(INTERNAL_RAG_INGEST_WORKFLOW),
    {
      workflowId: params.workflowId,
    },
  ) as string;
  return { ok: true, workflowId: params.workflowId, scheduledJobId, action: params.action };
}

export const ragIngestProcedures = {
  start: procedure.mutation
    .name(RAG_BUILTIN_OPERATION_NAMES.documentsIngestStart)
    .input(ragBuiltinInputSchemas.documentsIngestStart)
    .output(ragBuiltinOutputSchemas.documentsIngestStart)
    .handler(async ({ context: ctx, input: rawArgs }) => {
      const input: RagIngestInput = {
        storageId: rawArgs.storageId,
        corpus: rawArgs.corpus.trim(),
        visibility: normalizeVisibility(rawArgs.visibility),
        ownerUserId: rawArgs.ownerUserId?.trim() || undefined,
        sourceKey: rawArgs.sourceKey?.trim() || undefined,
        metadata: normalizeMetadata(rawArgs.metadata),
        reindexMode: normalizeReindexMode(rawArgs.reindexMode),
        mode: normalizeMode(rawArgs.mode),
        provider: normalizeProvider(rawArgs.provider),
      };
      return startRagIngestJob(ctx, input);
    }),
  reindex: procedure.mutation
    .name(RAG_BUILTIN_OPERATION_NAMES.documentsIngestReindex)
    .input(ragBuiltinInputSchemas.documentsIngestReindex)
    .output(ragBuiltinOutputSchemas.documentsIngestReindex)
    .handler(async ({ context: ctx, input: rawArgs }) => {
      const viewer = ctx.auth.requireAuth();
      const source = await loadAccessibleRagSource(ctx.unsafeDb, {
        sourceId: rawArgs.sourceId,
        viewerUserId: viewer.id,
      });
      if (!source) {
        throw new Error(`rag source not found: ${rawArgs.sourceId}`);
      }
      return startRagIngestJob(ctx, {
        storageId: source.storage_id,
        corpus: source.corpus,
        visibility: source.visibility_scope,
        ownerUserId: source.owner_user_id ?? undefined,
        sourceKey: source.source_key,
        metadata: source.metadata as Record<string, unknown>,
        reindexMode: "force",
        mode: normalizeMode(rawArgs.mode),
        provider: normalizeProvider(rawArgs.provider),
      });
    }),
  getJob: procedure.query
    .name(RAG_BUILTIN_OPERATION_NAMES.ragIngestJobsGet)
    .input(ragBuiltinInputSchemas.ragIngestJobsGet)
    .output(ragBuiltinOutputSchemas.ragIngestJobsGet)
    .handler(async ({ context: ctx, input: args }) => {
      const viewer = ctx.auth.requireAuth();
      return loadAccessibleRagIngestJob(ctx.unsafeDb, {
        jobId: args.id,
        viewerUserId: viewer.id,
      });
    }),
  listJobs: procedure.query
    .name(RAG_BUILTIN_OPERATION_NAMES.ragIngestJobsList)
    .input(ragBuiltinInputSchemas.ragIngestJobsList)
    .output(ragBuiltinOutputSchemas.ragIngestJobsList)
    .handler(async ({ context: ctx, input: args }) => {
      const viewer = ctx.auth.requireAuth();
      const limit = args.limit == null ? 20 : Math.max(1, Math.min(100, Math.floor(args.limit)));
      const status = normalizeJobStatus(args.status);
      return listAccessibleRagIngestJobs(ctx.unsafeDb, {
        viewerUserId: viewer.id,
        limit,
        status,
        corpus: args.corpus?.trim() || undefined,
      });
    }),
  cancelJob: procedure.mutation
    .name(RAG_BUILTIN_OPERATION_NAMES.ragIngestJobsCancel)
    .input(ragBuiltinInputSchemas.ragIngestJobsById)
    .output(ragBuiltinOutputSchemas.ragIngestJobsCancel)
    .handler(async ({ context: ctx, input: args }) => {
      const viewer = ctx.auth.requireAuth();
      const job = await loadAccessibleRagIngestJob(ctx.unsafeDb, {
        jobId: args.id,
        viewerUserId: viewer.id,
      });
      if (!job) {
        return { ok: false, workflowId: null };
      }

      const updated = await ctx.unsafeDb.execute(sql`
            UPDATE _gencow_workflows
            SET
              status = 'failed',
              error = 'Cancelled by user', error_code = NULL,
              completed_at = NOW(),
              updated_at = NOW()
            WHERE id = ${job.workflowId}
              AND status IN ('pending', 'running')
            RETURNING id
        `);
      const row = rowsFromResult<{ id: string }>(updated)[0] ?? null;
      if (!row) {
        return { ok: false, workflowId: job.workflowId };
      }

      await finalizeRagIngestJob(ctx.unsafeDb, {
        jobId: job.id,
        status: "canceled",
        error: "Cancelled by user",
        metrics: job.metrics,
        providerTrace: job.providerTrace as Record<string, unknown>,
      });
      await markRagSourceFailed(ctx.unsafeDb, {
        sourceId: job.sourceId,
        provider: String((job.providerTrace as { provider?: string })?.provider ?? "local_text"),
        error: "Cancelled by user",
      });
      return { ok: true, workflowId: job.workflowId };
    }),
  retryJob: procedure.mutation
    .name(RAG_BUILTIN_OPERATION_NAMES.ragIngestJobsRetry)
    .input(ragBuiltinInputSchemas.ragIngestJobsById)
    .output(ragBuiltinOutputSchemas.ragIngestJobsRetry)
    .handler(async ({ context: ctx, input: args }) => {
      const viewer = ctx.auth.requireAuth();
      const job = await loadAccessibleRagIngestJob(ctx.unsafeDb, {
        jobId: args.id,
        viewerUserId: viewer.id,
      });
      if (!job) {
        return { ok: false, workflowId: null, scheduledJobId: null, action: "retry" };
      }

      await resetRagIngestJobForRetry(ctx.unsafeDb, {
        jobId: job.id,
        metrics: job.metrics,
        providerTrace: job.providerTrace as Record<string, unknown>,
      });

      return setWorkflowStatusAndSchedule(ctx, {
        workflowId: job.workflowId,
        action: "retry",
        updateSql: sql`
              UPDATE _gencow_workflows
              SET
                status = 'pending',
                retry_count = 0,
                result = NULL,
                error = NULL, error_code = NULL,
                current_step = NULL,
                handler_started_at = NULL,
                last_resume_attempt_at = NULL,
                started_at = NOW(),
                completed_at = NULL,
                updated_at = NOW()
              WHERE id = ${job.workflowId}
                AND status = 'failed'
              RETURNING id
          `,
      });
    }),
  resumeJob: procedure.mutation
    .name(RAG_BUILTIN_OPERATION_NAMES.ragIngestJobsResume)
    .input(ragBuiltinInputSchemas.ragIngestJobsById)
    .output(ragBuiltinOutputSchemas.ragIngestJobsResume)
    .handler(async ({ context: ctx, input: args }) => {
      const viewer = ctx.auth.requireAuth();
      const job = await loadAccessibleRagIngestJob(ctx.unsafeDb, {
        jobId: args.id,
        viewerUserId: viewer.id,
      });
      if (!job) {
        return { ok: false, workflowId: null, scheduledJobId: null, action: "resume" };
      }

      return setWorkflowStatusAndSchedule(ctx, {
        workflowId: job.workflowId,
        action: "resume",
        updateSql: sql`
              UPDATE _gencow_workflows
              SET updated_at = NOW()
              WHERE id = ${job.workflowId}
                AND status = 'pending'
              RETURNING id
          `,
      });
    }),
} as const;

export function registerRagIngestRuntime(): void {
  registerRagCorporaRuntime();
  registerRagOperationsRuntime();

  workflow(INTERNAL_RAG_INGEST_WORKFLOW, {
    public: false,
    args: {
      jobId: v.string(),
      sourceId: v.string(),
      storageId: v.string(),
      corpus: v.string(),
      visibility: v.string(),
      ownerUserId: v.optional(v.string()),
      sourceKey: v.optional(v.string()),
      metadata: v.optional(v.any()),
      reindexMode: v.optional(v.string()),
      mode: v.optional(v.string()),
      provider: v.optional(v.string()),
    },
    handler: async (wf, rawArgs) => {
      const visibility = normalizeVisibility(rawArgs.visibility);
      const mode = normalizeMode(rawArgs.mode);
      const provider = normalizeProvider(rawArgs.provider);
      const metadata = normalizeMetadata(rawArgs.metadata);

      await wf.step("validateInput", async () => {
        await setRagIngestJobStage(wf.unsafeDb, {
          jobId: rawArgs.jobId,
          status: "queued",
          stage: "validateInput",
        });
      });

      const stored = await wf.step("loadStoredFile", async () => {
        const loaded = await wf.storage.get(rawArgs.storageId);
        if (!loaded) {
          throw new Error(`storageId not found: ${rawArgs.storageId}`);
        }
        return loaded as StoredFileLike;
      });

      const converted = await wf.step("convertDocument", async () => {
        await setRagIngestJobStage(wf.unsafeDb, {
          jobId: rawArgs.jobId,
          status: "converting",
          stage: "convertDocument",
        });
        return wf.services.document.convert({
          storageId: rawArgs.storageId,
          filename: stored.name,
          mimeType: stored.type,
          corpus: rawArgs.corpus,
          visibility,
          ownerUserId: rawArgs.ownerUserId,
          mode,
          provider,
        });
      });

      const sections = await wf.step("deriveSections", async () => {
        await setRagIngestJobStage(wf.unsafeDb, {
          jobId: rawArgs.jobId,
          status: "chunking",
          stage: "deriveSections",
          providerTrace: converted.providerTrace as Record<string, unknown>,
        });
        return converted.sections.length > 0
          ? converted.sections
          : [
              {
                id: "section-1",
                depth: 1,
                path: [],
                charStart: 0,
                charEnd: converted.markdown.length,
              },
            ];
      });

      await wf.step("upsertCorpusAndSource", async () => {
        await setRagIngestJobStage(wf.unsafeDb, {
          jobId: rawArgs.jobId,
          status: "upserting",
          stage: "upsertCorpusAndSource",
          providerTrace: converted.providerTrace as Record<string, unknown>,
        });
        await persistConvertedRagSource(wf.unsafeDb, {
          sourceId: rawArgs.sourceId,
          provider: converted.providerTrace.provider,
          result: converted,
          metadata: {
            ...metadata,
            storageId: rawArgs.storageId,
            sourceKey: rawArgs.sourceKey?.trim() || stored.name || rawArgs.storageId,
          },
        });
      });

      const plannedChunks = await wf.step("planChunks", async () => {
        await setRagIngestJobStage(wf.unsafeDb, {
          jobId: rawArgs.jobId,
          status: "chunking",
          stage: "planChunks",
        });
        return createChunkPlan(converted.markdown || converted.text, sections);
      });

      const embeddings = await wf.step("embedChunkBatch", async () => {
        await setRagIngestJobStage(wf.unsafeDb, {
          jobId: rawArgs.jobId,
          status: "embedding",
          stage: "embedChunkBatch",
          metrics: {
            chunkCount: plannedChunks.length,
            batchCount: createRagEmbeddingBatches(plannedChunks).length,
          },
        });
        return embedChunks(wf, plannedChunks);
      });

      await wf.step("replaceSectionsAndChunks", async () => {
        await setRagIngestJobStage(wf.unsafeDb, {
          jobId: rawArgs.jobId,
          status: "upserting",
          stage: "replaceSectionsAndChunks",
        });
        await replaceRagSectionsAndChunks(wf.unsafeDb, {
          sourceId: rawArgs.sourceId,
          corpus: rawArgs.corpus,
          visibility,
          ownerUserId: rawArgs.ownerUserId ?? null,
          sections,
          chunks: plannedChunks.map((chunk, index) => ({
            ...chunk,
            embedding: embeddings[index] ?? null,
          })),
        });
      });

      await wf.step("recordUsageAndFinalize", async () => {
        await finalizeRagIngestJob(wf.unsafeDb, {
          jobId: rawArgs.jobId,
          status: "completed",
          metrics: {
            bytes: stored.size,
            sectionCount: sections.length,
            chunkCount: plannedChunks.length,
            warningCount: converted.warnings.length,
            externalProvider: converted.providerTrace.external,
          },
          providerTrace: converted.providerTrace as Record<string, unknown>,
        });
        await emitCompletedRagIngestMetrics({
          db: wf.unsafeDb,
          corpus: rawArgs.corpus,
          visibility,
          ownerUserId: rawArgs.ownerUserId ?? null,
          jobId: rawArgs.jobId,
          workflowId: rawArgs.jobId,
          sourceId: rawArgs.sourceId,
          storageBytes: stored.size,
          sectionCount: sections.length,
          chunkCount: plannedChunks.length,
          warningCount: converted.warnings.length,
          providerTrace: converted.providerTrace,
          reindexMode: rawArgs.reindexMode,
        });
      });

      return {
        sourceId: rawArgs.sourceId,
        jobId: rawArgs.jobId,
        chunkCount: plannedChunks.length,
      };
    },
  });
}
